/**
 * @fileoverview Room visualization and control components
 * 
 * Contains components for:
 * - RoomUnit: SVG visualization of a single room with radiator and valves
 * - RoomControls: Control panel for room parameters
 * 
 * @module HydraulicBalancing/components/RoomComponents
 * @author HeatCraftHP Team
 * @version 2.0.0
 */

import React, { useMemo, useState } from 'react';
import { Space, Input, Select, Tooltip, Drawer, Button } from 'antd';
import { DeleteOutlined, QuestionCircleOutlined, ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';

import { LatexRenderer } from '../../../Shared/LatexRenderer';
import { PredefinedValvePlusThermostatValve } from '../../../Shared/PredefinedValvePlusThermostatValve';

import {
  SVG_DIMENSIONS,
  COLORS,
  RADIATOR_CONFIG,
  BUILDING_CONFIG,
  VALVE_CONFIG,
} from '../constants';
import { RADIATOR_POWER_MATRIX } from '../../HeatPumpSimulation/constants';

import styles from '../HydraulicBalancing.module.css';

import {
  calculateRValue,
  formatNumber,
  massFlowToLPM,
} from '../utils';

// Import assets (local to HydraulicBalancing folder)
import radiator3_start from '../radiator 3 start.png';
import radiator3_middle from '../radiator 3 middle.png';
import radiator3_end from '../radiator 3 end.png';
import thermostat from '../thermostatRadiator.png';
import flowrateIcon from '../flowrate.png';
import presetValveIcon from '../prefixedValve.png';
import RadiatorTypesImage from '../../../Shared/RadiatorTypes.jpg';

const { ROOM_WIDTH, ROOM_HEIGHT } = SVG_DIMENSIONS;

// ============================================================================
// ROOM TYPE SELECTOR COMPONENT
// ============================================================================

/**
 * Room type selector with predefined temperature settings
 */
const RoomTypeSelector = ({ value, onChange, onTemperatureChange }) => {
  const roomTypes = [
    { key: "living", name: "Living Room", temperature: 20 },
    { key: "bathroom", name: "Bathroom", temperature: 24 },
  ];

  return (
    <Select
      optionLabelProp="label"
      value={value}
      style={{ width: 195 }}
      onChange={(roomKey) => {
        const room = roomTypes.find((r) => r.key === roomKey);
        if (room) {
          onTemperatureChange(room.temperature);
          onChange(roomKey);
        }
      }}
    >
      {roomTypes.map((room) => (
        <Select.Option
          key={room.key}
          value={room.key}
          label={(
            <div className="default-opposite-arrangement" style={{ width: 145 }} >
              <span className="default-gray">{room.name}</span>
              <span className="default-red">{room.temperature} °C</span>
            </div>
          )}
        >
          <Space className="default-opposite-arrangement">
            <span className="default-gray">{room.name}</span>
            <span className="default-red">{room.temperature} °C</span>
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

// ============================================================================
// U-PARAMETERS MODAL COMPONENT
// ============================================================================

/**
 * U-Parameters Configuration Modal
 * Allows detailed configuration of building envelope parameters
 */
const UParametersModal = ({
  visible,
  onClose,
  roofArea,
  roofU,
  wallArea,
  wallU,
  windowArea,
  windowU,
  onRoofAreaChange,
  onRoofUChange,
  onWallAreaChange,
  onWallUChange,
  onWindowAreaChange,
  onWindowUChange,
  onConfirm,
}) => {
  const handleNumberInput = (value, min, max, onChange) => {
    const num = parseFloat(value);
    const clamped = Math.max(min, Math.min(max, num));
    onChange(clamped);
  };

  const totalArea = roofArea + wallArea + windowArea;
  const weightedU = totalArea > 0 
    ? (roofArea * roofU + wallArea * wallU + windowArea * windowU) / totalArea 
    : 0;

  return (
    <Drawer
      title="Building Envelope Parameters"
      placement="left"
      width={'50%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Roof Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray" }}>Roof</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={roofArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>Area</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onRoofAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={roofU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>U-value</p>}
            suffix={<LatexRenderer latex="$ \frac{W}{K \cdot m^2 } $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onRoofUChange)}
          />
        </Space>

        {/* Wall Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray"}}>Wall</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={wallArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>Area</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onWallAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={wallU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>U-value</p>}
            suffix={<LatexRenderer latex="$ \frac{W}{K \cdot m^2 } $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onWallUChange)}
          />
        </Space>

        {/* Window Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray" }}>Windows</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={windowArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>Area</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onWindowAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={windowU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>U-value</p>}
            suffix={<LatexRenderer latex="$ \frac{W}{K \cdot m^2 } $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onWindowUChange)}
          />
        </Space>

        {/* Calculated Envelope U-Value */}
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#f5f5f5", 
          borderRadius: "4px",
          marginTop: "16px",
          borderLeft: "4px solid #1890ff"
        }}>
          <p style={{ margin: "0 0 8px 0", fontWeight: "500", color: "#333" }}>
            Weighted Envelope U-Value
          </p>
          <p style={{ margin: "12px 0 12px 0px", fontSize: "12px", color: "#666" }}>
            <LatexRenderer latex="$ \large{U_{envelope} = \frac{\sum A_i \cdot U_i}{\sum A_i}} $" />
          </p>
          <Space align="center">
            <p style={{ margin: 0, fontSize: "18px", color: "#1890ff", fontWeight: "bold" }}>
              {weightedU.toFixed(3)}
            </p>
            <LatexRenderer style={{ margin: 0, fontSize: "15px", color: "#666", fontWeight: "bold" }} latex="$ \frac{W}{K \cdot m^2 } $" />
          </Space>
        </div>
        
        <Button type="primary" size="small" onClick={onConfirm}>
          Confirm
        </Button>
      </Space>
    </Drawer>
  );
};

// ============================================================================
// RADIATOR TYPES MODAL COMPONENT
// ============================================================================

/**
 * Radiator Types Information Modal
 * Displays radiator types reference image
 */
const RadiatorTypesModal = ({ visible, onClose }) => {
  return (
    <Drawer
      title="Radiator Types"
      placement="left"
      width={'100%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <img src={RadiatorTypesImage} alt="Radiator Types" style={{ width: "100%", height: "97%", objectFit: "contain" }} />
    </Drawer>
  );
};

// ============================================================================
// ROOM UNIT SVG COMPONENT
// ============================================================================

/**
 * SVG visualization of a single room within the building
 * 
 * Displays:
 * - Room outline and number
 * - Current and desired temperature
 * - Radiator with adjustable sections
 * - Thermostat valve indicator
 * - Preset valve indicator
 * - Flow rate display
 * - Piping connections
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.roomNumber - Room number (1-indexed)
 * @param {Object} props.room - Room state object
 * @param {number} props.x - SVG x position
 * @param {number} props.y - SVG y position
 * @param {number} props.floorIndex - Floor number
 * @param {number} props.colIndex - Position on floor
 * @param {boolean} props.isSelected - Whether this room is currently selected
 * @param {Function} props.onSelect - Callback when room is clicked
 */
export const RoomUnit = React.memo(({
  roomNumber,
  room,
  x,
  y,
  floorIndex,
  colIndex,
  isSelected,
  onSelect,
}) => {
  const { Treq, mFlow, Tc, nRad, kThermostat, valve } = room;
  
  // Get current temperature
  const currentTemp = Tc[Tc.length - 1];
  
  // Calculate color strength based on valve opening
  const colorStrength = 0.2 + kThermostat * 0.3;
  
  // Memoize expensive calculations
  const flowRateLPM = useMemo(() => 
    massFlowToLPM(mFlow[mFlow.length - 1] || 0),
    [mFlow]
  );

  return (
    <svg
      x={x}
      y={y}
      width={ROOM_WIDTH}
      height={ROOM_HEIGHT}
      style={{ cursor: 'pointer' }}
    >
      {/* Room background and border */}
      <rect
        x={0}
        y={0}
        width={ROOM_WIDTH}
        height={ROOM_HEIGHT}
        fill="transparent"
        stroke={COLORS.BORDER}
        strokeWidth={isSelected ? 2 : 1}
        onClick={() => onSelect(floorIndex, colIndex)}
      />
      
      {/* Room number indicator */}
      <g>
        {isSelected && (
          <rect
            x={5}
            y={5}
            width={roomNumber > 9 ? 26 : 18}
            height={20}
            fill="rgba(11, 117, 189, 0.25)"
            stroke="rgba(11, 117, 189, 0.5)"
            rx={3}
          />
        )}
        <text
          x={10}
          y={20}
          fill={isSelected ? COLORS.PRIMARY : COLORS.GRAY}
          fontSize={isSelected ? 15 : 12}
          fontWeight={isSelected ? 'bold' : 'normal'}
        >
          {roomNumber}
        </text>
      </g>
      
      {/* Temperature display */}
      <g>
        <text x={150} y={20} fill={COLORS.HOT} fontSize={14}>
          {formatNumber(currentTemp, 1)}
        </text>
        <text x={180} y={20} fill={COLORS.HOT} fontSize={14}>
          °C
        </text>
        <text x={157} y={30} fill={COLORS.GRAY} fontSize={10}>
          → {formatNumber(Treq, 1)}
        </text>
      </g>
      
      {/* Radiator visualization */}
      <g transform="translate(20, 90)">
        {/* Radiator start section */}
        <image
          href={radiator3_start}
          x={0}
          y={0}
          width={14.5}
        />
        
        {/* Radiator middle sections */}
        {Array.from({ length: nRad }).map((_, i) => (
          <image
            key={i}
            href={radiator3_middle}
            x={14.5 + 7 * i}
            y={0}
            width={7}
          />
        ))}
        
        {/* Radiator end section */}
        <image
          href={radiator3_end}
          x={14.5 + 7 * nRad}
          y={0}
          width={14.5}
        />
      </g>
      
      {/* Thermostat valve visualization */}
      <Tooltip color="white" title="Thermostat Valve">
        <g>
          <image
            href={thermostat}
            width={25}
            x={150}
            y={ROOM_HEIGHT * 0.55}
          />
          
          {/* Thermostat opening indicator */}
          <rect
            x={178}
            y={100 - kThermostat * 15}
            height={kThermostat * 15}
            width={5}
            fill="rgba(255, 165, 0, 0.5)"
          />
          <rect
            x={177}
            y={84}
            height={17}
            width={7}
            fill="transparent"
            stroke="rgba(255, 165, 0, 1)"
          />
          <line
            x1={178}
            x2={183}
            y1={100 - kThermostat * 15}
            y2={100 - kThermostat * 15}
            stroke="orange"
            strokeWidth={2}
          />
        </g>
      </Tooltip>
      
      {/* Piping - Supply (hot) */}
      <g>
        <rect
          x={167}
          y={125}
          height={4}
          width={33}
          fill={`rgba(255, 0, 0, ${colorStrength})`}
        />
        <rect
          x={163}
          y={122}
          height={7}
          width={4}
          fill={`rgba(255, 0, 0, ${colorStrength})`}
        />
        <rect
          x={42 + 7 * nRad}
          y={111}
          height={4}
          width={109 - 7 * nRad}
          fill={`rgba(255, 0, 0, ${colorStrength})`}
        />
      </g>
      
      {/* Piping - Return (cold) */}
      <g>
        <rect
          x={42 + 7 * nRad}
          y={120}
          height={4}
          width={30}
          fill={`rgba(70, 130, 180, ${colorStrength})`}
        />
        <rect
          x={72 + 7 * nRad}
          y={120}
          height={45}
          width={4}
          fill={`rgba(70, 130, 180, ${colorStrength})`}
        />
      </g>
      
      {/* Flow rate display */}
      <g>
        <image
          href={flowrateIcon}
          width={17}
          x={8}
          y={40}
        />
        <text
          x={28}
          y={51}
          fill={`rgba(255, 0, 0, ${colorStrength * 2})`}
          fontSize={12}
        >
          {formatNumber(flowRateLPM, 2)}
        </text>
        <text x={28} y={61} fill={COLORS.GRAY} fontSize={10}>
          L/min
        </text>
      </g>
      
      {/* Preset valve indicator */}
      <Tooltip color="white" title="Preset Valve Position">
        <g>
          <image
            href={presetValveIcon}
            width={20}
            x={31}
            y={93}
            opacity={0.75}
          />
          <text x={53} y={107} fill="black" fontSize={11}>
            {formatNumber(valve, 2)}
          </text>
        </g>
      </Tooltip>
    </svg>
  );
});

RoomUnit.displayName = 'RoomUnit';

// ============================================================================
// ROOM CONTROLS PANEL COMPONENT
// ============================================================================

/**
 * Control panel for managing selected room parameters
 * 
 * Allows adjustment of:
 * - Desired room temperature
 * - Radiator size (number of sections)
 * - U-value to outdoor
 * - Preset valve opening
 * - Displays calculated values (R-value, nominal power, etc.)
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.room - Selected room state
 * @param {number} props.floorIndex - Floor number
 * @param {number} props.colIndex - Position on floor
 * @param {number} props.roomNumber - Room number (1-indexed)
 * @param {Array} props.roomOptions - List of available rooms for selection
 * @param {number} props.kRad - Radiator coefficient
 * @param {number} props.tSupply - Supply temperature
 * @param {number} props.totalFloors - Total floors in building
 * @param {number} props.systemPressure - System differential pressure [bar]
 * @param {number} props.pipeDiameter - Pipe diameter [mm]
 * @param {boolean} props.canDelete - Whether room can be deleted
 * @param {Function} props.onSelectRoom - Room selection callback
 * @param {Function} props.onUpdateRoom - Room update callback
 * @param {Function} props.onDeleteRoom - Room deletion callback
 */
export const RoomControls = ({
  room,
  floorIndex,
  colIndex,
  roomNumber,
  roomOptions,
  kRad,
  tSupply,
  totalFloors,
  systemPressure,
  pipeDiameter,
  canDelete,
  onSelectRoom,
  onUpdateRoom,
  onDeleteRoom,
}) => {
  const { /*Tc,*/ Treq, valve, Uout, area, Tout, mFlow, kThermostat } = room;
  // Derive selected room type from current temperature
  const getSelectedRoomType = (temp) => {
    if (temp === 20) return "living";
    if (temp === 24) return "bathroom";
    return "choose";
  };
  
  const [selectedRoomType, setSelectedRoomType] = React.useState(getSelectedRoomType(Treq));
  const [showRadiatorTypes, setShowRadiatorTypes] = useState(false);
  const [radiatorType, setRadiatorType] = useState("22");
  const [radiatorSize, setRadiatorSize] = useState(80);
  
  // U-Parameters modal state
  const [showUParameters, setShowUParameters] = useState(false);
  const [roofArea, setRoofArea] = useState(0);
  const [roofU, setRoofU] = useState(0.2);
  const [wallArea, setWallArea] = useState(BUILDING_CONFIG.WALL_AREA * 0.7);
  const [wallU, setWallU] = useState(0.3);
  const [windowArea, setWindowArea] = useState(BUILDING_CONFIG.WALL_AREA * 0.3);
  const [windowU, setWindowU] = useState(1.5);
  
  // Current temperature
  //const currentTemp = Tc[Tc.length - 1];
  
  // Calculated values
  const nominalPower = useMemo(() => 
    (RADIATOR_POWER_MATRIX[radiatorSize * 10]?.[radiatorType] || 0) / 1000,
    [radiatorSize, radiatorType]
  );
  
  const rValue = useMemo(() => 
    calculateRValue(Uout, area || BUILDING_CONFIG.WALL_AREA),
    [Uout, area]
  );

  const currentFlowRate = massFlowToLPM(mFlow[mFlow.length - 1] || 0);

  return (
    <div className={styles.roomControlsPanel}>
      {/* Room Selection Header */}
      <Space direction="horizontal" size="middle" style={{ width: '100%', justifyContent: 'space-between' }} align="center">
        <Select
          key={`room-select-${floorIndex}-${colIndex}`}
          value={`${floorIndex},${colIndex}`}
          bordered={false}
          style={{ 
            width: 'auto',
            minWidth: 100
          }}
          onChange={(value) => {
            const [floor, col] = value.split(',').map(Number);
            onSelectRoom([floor, col]);
          }}
          optionLabelProp="display"
          options={roomOptions.map(([i, j]) => {
            const roomNum = i * BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR + j + 1;
            return {
              label: (
                <Space size="small" align="center">
                  <span style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                    color: 'white',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    display: 'inline-block',
                    width:'100%',
                  }}>
                    #{roomNum}
                  </span>
                  
                </Space>
              ),
              display: (
                <Space size={4} align="center" style={{ display: 'flex' }}>
                  <span style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                    color: 'white',
                    padding: '3px 11px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 6px rgba(24, 144, 255, 0.25)',
                    display: 'inline-block'
                  }}>
                    #{roomNumber}
                  </span>
                  
                </Space>
              ),
              value: `${i},${j}`,
            };
          })}
          dropdownStyle={{ minWidth: '100px' }}
        />
        
        {/* Delete button */}
        <Tooltip
          color="white"
          title={canDelete ? 'Delete this room' : 'Cannot delete this room'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: canDelete ? 'rgba(255, 77, 79, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            cursor: canDelete ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            border: `1px solid ${canDelete ? 'rgba(255, 77, 79, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
          onMouseEnter={(e) => {
            if (canDelete) {
              e.currentTarget.style.background = 'rgba(255, 77, 79, 0.15)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (canDelete) {
              e.currentTarget.style.background = 'rgba(255, 77, 79, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
          onClick={() => canDelete && onDeleteRoom(floorIndex, colIndex)}
          >
            <DeleteOutlined
              style={{
                fontSize: '16px',
                color: canDelete ? '#ff4d4f' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          </div>
        </Tooltip>
      </Space>
      
      <div className={styles.controlsDivider} />
      
      {/* Room Parameters */}
      <Space direction="vertical" size="middle" align="start" style={{ width: '100%' }}>
        {/* Desired Temperature */}
        <Space direction="vertical" align="start">
          <small className="default-title">Desired room temperature</small>
          <Space size="large">
            <Input
              type="number"
              min={10}
              max={30}
              value={Treq}
              style={{ width: 160 }}
              suffix="°C"
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 20;
                const clampedValue = Math.max(10, Math.min(30, value));
                onUpdateRoom('Treq', clampedValue);

                // Auto-select room type if it matches
                if (clampedValue === 20) setSelectedRoomType("living");
                else if (clampedValue === 24) setSelectedRoomType("bathroom");
                else setSelectedRoomType("Choose room type");
              }}
            />

            <RoomTypeSelector
              value={selectedRoomType}
              onChange={setSelectedRoomType}
              onTemperatureChange={(temp) => onUpdateRoom('Treq', temp)}
            />
          </Space>
        </Space>
        
        {/* Radiator Type/Size */}
        <Space direction="vertical" align="start">
          <small className="default-title">Radiator type/size</small>
          <Space align="center" size="large">
            <Select
              value={radiatorType}
              style={{ width: 160 }}
              onChange={(type) => {
                setRadiatorType(type);
                // Type affects heat output
              }}
            >
              {["10", "11", "21", "22", "33"].map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>

            <Select
              value={radiatorSize}
              style={{ width: 160 }}
              onChange={(size) => {
                setRadiatorSize(size);
                // Derive nRad from size: roughly 1 section per 70-80cm
                const sections = Math.round(size / 7.5);
                onUpdateRoom('nRad', Math.max(RADIATOR_CONFIG.MIN_SECTIONS, Math.min(RADIATOR_CONFIG.MAX_SECTIONS, sections)));
              }}
            >
              {[40, 50, 60, 70, 80, 90, 100].map((size) => (
                <Select.Option key={size} value={size}>
                  {size} cm
                </Select.Option>
              ))}
            </Select>

            <QuestionCircleOutlined
              className="default-gray hq-clickable"
              title="Radiator types"
              onClick={() => setShowRadiatorTypes(true)}
            />

            <Space>
              <p className="default-gray">P-nominal</p>
              <LatexRenderer latex={`$\\small{${formatNumber(nominalPower, 2)}}$`} />
              <LatexRenderer latex={"$\\small{kW}$"} />
            </Space>
          </Space>         
        </Space>
        
        {/* Building Thermal Properties */}
        <Space direction="vertical" align="start" size="small" style={{ width: '100%' }}>
          <small className="default-title">U-value: building thermal properties</small>
          <Space align="center" size="large">
            <Input
              type="number"
              min={0.1}
              max={5}
              step={0.05}
              value={Uout}
              style={{ width: 160, textAlign: "center" }}
              prefix={
                <Tooltip color="white" title={<p>Average U-value for the room</p>}>
                  <div style={{ marginRight: 15 }}>
                    <LatexRenderer latex="$ \overline{U} $" />
                  </div>
                </Tooltip>
              }
              suffix={<LatexRenderer latex="$ \frac{W}{K \cdot m^2 } $" />}
              onChange={(e) => {
                const value = Math.max(0.1, Math.min(5, parseFloat(e.target.value) || 1));
                onUpdateRoom('Uout', value);
              }}
            />

            <Input
              type="number"
              min={0}
              step={1}
              value={area || BUILDING_CONFIG.WALL_AREA}
              style={{ width: 160, textAlign: "center" }}
              prefix={
                <Tooltip color="white" title={<p>Total area of the room</p>}>
                  <div style={{ marginRight: 15 }}>
                    <LatexRenderer latex="$ A $" />
                  </div>
                </Tooltip>
              }
              suffix={<LatexRenderer latex="$ m^2 $" />}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (value > 0) onUpdateRoom('area', value);
              }}
            />

            <SettingOutlined
              className="default-gray hq-clickable"
              title="Specify sub-parameters"
              onClick={() => setShowUParameters(true)}
            />

            <Tooltip
              color="white"
              title={
                <Space direction="vertical" align="start">
                  <LatexRenderer latex="$\color{black}{R = 1 / (A \cdot \overline{U}) } $" />
                  <LatexRenderer latex="$\color{black}{\dot{Q}_\text{loss} = \overline{U} \cdot A \cdot (T_\text{room} - T_\text{outdoor})} $" />
                  <LatexRenderer latex="$\color{black}{\dot{Q}_\text{loss} = \large{\frac{T_\text{room} - T_\text{outdoor}}{R}}}$" />
                </Space>
              }
            >
              <Space>
                <p className="default-gray">R-value</p>
                <LatexRenderer latex={`$\\small{${formatNumber(rValue, 5)}}$`} />
                <LatexRenderer latex={"$\\small{\\frac{K}{W}}$"} />
              </Space>
            </Tooltip>
          </Space>          
        </Space>
        
        {/* Preset Valve + Thermostat Valve with Operating Status */}
        <Space direction="vertical" align="start" style={{ width: '100%' }}>
          <small className="default-title">Valve control</small>
          {/* Recommended preset: floor {floorIndex + 1}, position {colIndex + 1}: {formatNumber(recommendedPreset, 2)} */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}>
              <PredefinedValvePlusThermostatValve
                d={kThermostat ?? 0}
                temp={Treq}
                value={Math.round(valve * VALVE_CONFIG.PRESET_POSITIONS)}
                onChange={(v) => onUpdateRoom('valve', v / VALVE_CONFIG.PRESET_POSITIONS)}
                onTemperatureChange={(t) => onUpdateRoom('Treq', t)}
                isHighlighted={true}
              />
            </div>
            
            {/* Operating Status - Right side */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              paddingTop: '8px',
              borderLeft: '1px solid rgba(0,0,0,0.06)',
              paddingLeft: '16px',
              marginLeft: '-60px',
              minWidth: '90px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '10px', color: '#999', letterSpacing: '0.5px' }}>SUPPLY</span>
                <span style={{ fontSize: '16px', fontWeight: '500', color: '#e63946' }}>{formatNumber(tSupply, 0)}°</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '10px', color: '#999', letterSpacing: '0.5px' }}>RETURN</span>
                <span style={{ fontSize: '16px', fontWeight: '500', color: '#457b9d' }}>{formatNumber(Tout, 1)}°</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '10px', color: '#999', letterSpacing: '0.5px' }}>FLOW</span>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#555' }}>{formatNumber(currentFlowRate, 2)}<span style={{ fontSize: '10px', fontWeight: '400', marginLeft: '2px' }}>L/m</span></span>
              </div>
            </div>
          </div>
        </Space>
      </Space>

      {/* U-Parameters Modal */}
      <UParametersModal
        visible={showUParameters}
        onClose={() => setShowUParameters(false)}
        roofArea={roofArea}
        roofU={roofU}
        wallArea={wallArea}
        wallU={wallU}
        windowArea={windowArea}
        windowU={windowU}
        onRoofAreaChange={setRoofArea}
        onRoofUChange={setRoofU}
        onWallAreaChange={setWallArea}
        onWallUChange={setWallU}
        onWindowAreaChange={setWindowArea}
        onWindowUChange={setWindowU}
        onConfirm={() => {
          const totalArea = roofArea + wallArea + windowArea;
          if (totalArea > 0) {
            const weightedU = (roofArea * roofU + wallArea * wallU + windowArea * windowU) / totalArea;
            onUpdateRoom('Uout', parseFloat(weightedU.toFixed(3)));
            onUpdateRoom('area', totalArea);
          }
          setShowUParameters(false);
        }}
      />
      
      {/* Radiator Types Modal */}
      <RadiatorTypesModal visible={showRadiatorTypes} onClose={() => setShowRadiatorTypes(false)} />
    </div>
  );
};

// ============================================================================
// ADD ROOM PLACEHOLDER COMPONENT
// ============================================================================

/**
 * Placeholder button for adding a new room at a position
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.x - SVG x position
 * @param {number} props.y - SVG y position
 * @param {number} props.floorIndex - Floor number
 * @param {number} props.colIndex - Position on floor
 * @param {boolean} props.canAdd - Whether room can be added here
 * @param {Function} props.onAdd - Callback when clicked
 */
export const AddRoomPlaceholder = ({
  x,
  y,
  floorIndex,
  colIndex,
  canAdd,
  onAdd,
}) => {
  if (!canAdd) return null;
  
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={() => onAdd(floorIndex, colIndex)}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={0}
        y={0}
        width={ROOM_WIDTH}
        height={ROOM_HEIGHT}
        fill="transparent"
        stroke={COLORS.BORDER}
        strokeWidth={1}
        strokeDasharray="5,5"
        rx={4}
      />
      <circle
        cx={ROOM_WIDTH / 2}
        cy={ROOM_HEIGHT / 2}
        r={20}
        fill="rgba(24, 144, 255, 0.1)"
        stroke={COLORS.SECONDARY}
        strokeWidth={2}
      />
      <text
        x={ROOM_WIDTH / 2}
        y={ROOM_HEIGHT / 2 + 6}
        textAnchor="middle"
        fill={COLORS.SECONDARY}
        fontSize={24}
        fontWeight="bold"
      >
        +
      </text>
      <text
        x={ROOM_WIDTH / 2}
        y={ROOM_HEIGHT / 2 + 35}
        textAnchor="middle"
        fill={COLORS.GRAY}
        fontSize={10}
      >
        Add Room
      </text>
    </g>
  );
};

export default RoomUnit;
